import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { lunchMenu as fallbackLunchMenu } from '../data/selfServiceContent';
import { formatMenuDate, getMonthKey, normalizeLunchMenu, toDateKey } from '../utils/menuSpreadsheet';

// JS getDay() (0=Pazar) -> Türkçe gün adı
const getTodayName = () => {
  const map = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  return map[new Date().getDay()];
};

const YemekhanePage = () => {
  const [lunchMenu, setLunchMenu] = useState(() => normalizeLunchMenu(fallbackLunchMenu, getMonthKey()));
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => toDateKey(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(() => getMonthKey());

  useEffect(() => {
    let active = true;

    const fetchMenu = async () => {
      try {
        setLoading(true);
        const response = await api.getManagedContent('selfService');
        const payload = response?.data || response?.content || {};
        const menu = payload.lunchMenus?.[selectedMonth] || payload.lunchMenu;

        if (active && Array.isArray(menu) && menu.length > 0) {
          setLunchMenu(normalizeLunchMenu(menu, selectedMonth));
        }
      } catch (error) {
        console.error('Yemekhane menüsü yüklenemedi, varsayılan menü kullanılıyor:', error);
        if (active) setLunchMenu(normalizeLunchMenu(fallbackLunchMenu, selectedMonth));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchMenu();
    return () => {
      active = false;
    };
  }, [selectedMonth]);

  // Menüyü hafta günü sırasına göre diz
  const orderedMenu = useMemo(() => {
    return [...lunchMenu].sort((a, b) => (a.date || a.day).localeCompare(b.date || b.day));
  }, [lunchMenu]);

  const todayName = getTodayName();
  const todayMenu = useMemo(
    () => orderedMenu.find((item) => item.date === toDateKey(new Date())) || orderedMenu.find((item) => item.day === todayName),
    [orderedMenu, todayName]
  );

  const activeMenu = useMemo(
    () => orderedMenu.find((item) => item.date === selectedDate) || todayMenu || orderedMenu[0],
    [orderedMenu, selectedDate, todayMenu]
  );

  if (loading) {
    return (
      <div className="modern-loading">
        <div className="text-center">
          <div className="modern-spinner spinner-border mb-3" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="text-muted">Yemekhane menüsü yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      {/* Doküman Merkezi tarzı başlık */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4 corporate-documents-hero">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <h3 className="fw-bold text-dark mb-1">Yemekhane</h3>
                <p className="text-muted mb-0">Haftalık yemek menüsü ve servis saatleri</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="badge bg-primary rounded-pill px-3 py-2">
                  <i className="bx bxs-dish me-1"></i>
                  {orderedMenu.length} Menü Günü
                </div>
                {todayMenu?.serviceWindow && (
                  <div className="badge bg-light text-dark rounded-pill px-3 py-2">
                    <i className="bx bx-time-five me-1"></i>
                    Servis: {todayMenu.serviceWindow}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bugünün menüsü öne çıkan kart */}
      {activeMenu && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="modern-card corporate-category-card">
              <div className="corporate-category-card-header">
                <h5 className="mb-0">
                  {activeMenu.date === toDateKey(new Date()) ? 'Bugünün Menüsü' : `${formatMenuDate(activeMenu.date)} · ${activeMenu.day}`}
                </h5>
                <span>
                  <i className="bx bxs-dish"></i>
                </span>
              </div>
              <div className="row g-3 mt-1">
                <MenuItem icon="bx bx-bowl-hot" label="Çorba" value={activeMenu.soup} />
                <MenuItem icon="bx bxs-dish" label="Ana Yemek" value={activeMenu.main} />
                <MenuItem icon="bx bx-restaurant" label="Yan Yemek" value={activeMenu.side} />
                <MenuItem icon="bx bx-cake" label="Ekstra / Tatlı" value={activeMenu.extra} />
              </div>
              {activeMenu.serviceWindow && (
                <p className="text-muted mt-3 mb-0">
                  <i className="bx bx-time-five me-1"></i>
                  Servis saatleri: <strong>{activeMenu.serviceWindow}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gün filtreleri + haftalık tablo */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4">
            <div className="cafeteria-month-toolbar">
              <label htmlFor="cafeteriaMonth">Menü ayı</label>
              <input id="cafeteriaMonth" type="month" value={selectedMonth} onChange={(event) => { setSelectedMonth(event.target.value); setSelectedDate(`${event.target.value}-01`); }} />
            </div>
            <div className="corporate-category-filters cafeteria-day-filters mb-4">
              {orderedMenu.map((item) => (
                <button
                  key={item.date || item.day}
                  className={`corporate-filter-chip ${selectedDate === item.date ? 'active' : ''}`}
                  onClick={() => setSelectedDate(item.date)}
                >
                  <i className="bx bx-calendar"></i>
                  <span>{formatMenuDate(item.date)}</span>
                  <small>{item.date === toDateKey(new Date()) ? 'Bugün' : item.day}</small>
                </button>
              ))}
            </div>

            <div className="table-responsive">
              <table className="table table-hover mb-0 modern-table">
                <thead>
                  <tr>
                    <th className="modern-table-header">Gün</th>
                    <th className="modern-table-header">Çorba</th>
                    <th className="modern-table-header">Ana Yemek</th>
                    <th className="modern-table-header">Yan Yemek</th>
                    <th className="modern-table-header">Ekstra</th>
                    <th className="modern-table-header">Servis Saati</th>
                  </tr>
                </thead>
                <tbody>
                  {orderedMenu.map((item) => (
                    <tr
                      key={item.date || item.day}
                      className={`modern-table-row ${item.date === toDateKey(new Date()) ? 'table-active' : ''}`}
                    >
                      <td className="modern-table-cell">
                        <div className="fw-medium text-dark">{formatMenuDate(item.date)} · {item.day}</div>
                        {item.date === toDateKey(new Date()) && (
                          <span className="modern-badge modern-badge-secondary">Bugün</span>
                        )}
                      </td>
                      <td className="modern-table-cell text-muted">{item.soup || '-'}</td>
                      <td className="modern-table-cell text-muted">{item.main || '-'}</td>
                      <td className="modern-table-cell text-muted">{item.side || '-'}</td>
                      <td className="modern-table-cell text-muted">{item.extra || '-'}</td>
                      <td className="modern-table-cell text-muted">{item.serviceWindow || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {orderedMenu.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="modern-empty-state text-center py-5">
              <i className="bx bx-dish display-1 text-muted mb-4" style={{ color: '#e9ecef' }}></i>
              <h4 className="fw-bold text-dark mb-3">Menü bulunamadı</h4>
              <p className="text-muted mb-0">Bu hafta için henüz bir yemek menüsü tanımlanmamış.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuItem = ({ icon, label, value }) => (
  <div className="col-md-6 col-lg-3">
    <div className="d-flex align-items-start gap-3">
      <div className="modern-file-icon">
        <i className={`${icon} text-primary`}></i>
      </div>
      <div>
        <small className="text-muted d-block">{label}</small>
        <div className="fw-medium text-dark">{value || '-'}</div>
      </div>
    </div>
  </div>
);

export default YemekhanePage;
